import { CheckCircle2, Circle } from 'lucide-react'
import { type ReactElement } from 'react'
import { type AgentMarkdownOptions, renderAgentInline } from './renderAgentInline'

export type { AgentMarkdownOptions } from './renderAgentInline'

const HR_REGEX = /^(-{3,}|\*{3,}|_{3,})$/
const H2_REGEX = /^## (.+)$/
const H3_REGEX = /^### (.+)$/
const BULLET_REGEX = /^[-*] (.+)$/
const NUMBERED_REGEX = /^\d+\.\s+(.+)$/
const CODE_FENCE_REGEX = /^```(\w*)$/
const TABLE_ROW_REGEX = /^\|(.+)\|$/
const TABLE_SEP_REGEX = /^\|[\s:]*-{2,}[\s:]*(\|[\s:]*-{2,}[\s:]*)+\|$/
const CHECKBOX_CHECKED_REGEX = /^\[([Xx✓])\]\s+(.+)$/
const CHECKBOX_UNCHECKED_REGEX = /^\[\s*\]\s+(.+)$/

function getSectionLabelColor(text: string): string {
    const lower = text.toLowerCase()
    if (lower.includes('completed') || lower.includes('done')) return 'text-green-600 dark:text-green-400'
    if (lower.includes('waiting') || lower.includes('pending')) return 'text-amber-600 dark:text-amber-500'
    if (lower.includes('failed') || lower.includes('error')) return 'text-red-600 dark:text-red-400'
    if (lower.includes('active') || lower.includes('in progress')) return 'text-blue-600 dark:text-blue-400'
    return 'text-slate-500 dark:text-slate-400'
}

/**
 * An agent reply rendered from Markdown without a library: headings, lists, checklists, tables, fenced code, rules,
 * emphasis, inline code and, opt-in (`options.links`), http(s)/mailto links opened in a new tab. Raw HTML is never rendered (React escapes
 * every string) and images show only their alt text. `#N` load jumps are opt-in (`options.loadRefJump`).
 */
export const renderAgentMarkdown = (content: string, options: AgentMarkdownOptions = {}): ReactElement => {
    if (!content) return <span />
    const renderInline = (text: string, key: string): ReactElement => renderAgentInline(text, key, options)

    const lines = content.split('\n')
    const elements: ReactElement[] = []
    let i = 0

    while (i < lines.length) {
        const line = lines[i]
        const trimmed = line.trim()

        // Skip empty lines
        if (!trimmed) {
            i++
            continue
        }

        // Fenced code block
        const fenceMatch = trimmed.match(CODE_FENCE_REGEX)
        if (fenceMatch) {
            const lang = fenceMatch[1]
            const codeLines: string[] = []
            i++
            while (i < lines.length && !lines[i].trim().match(/^```$/)) {
                codeLines.push(lines[i])
                i++
            }
            i++ // skip closing ```
            elements.push(
                <pre key={`code-${i}`} className="my-1.5 rounded-lg bg-gray-900 dark:bg-black/40 p-3 overflow-x-auto">
                    <code className="text-[0.8em] text-gray-200 font-mono leading-relaxed">
                        {lang && <span className="block text-[10px] text-gray-500 mb-1 uppercase">{lang}</span>}
                        {codeLines.join('\n')}
                    </code>
                </pre>
            )
            continue
        }

        // Markdown table — detect header row followed by separator row
        if (TABLE_ROW_REGEX.test(trimmed) && i + 1 < lines.length && TABLE_SEP_REGEX.test(lines[i + 1].trim())) {
            const headerCells = trimmed
                .split('|')
                .filter(Boolean)
                .map((c) => c.trim())
            i += 2 // skip header + separator

            const bodyRows: string[][] = []
            while (i < lines.length && TABLE_ROW_REGEX.test(lines[i].trim())) {
                bodyRows.push(
                    lines[i]
                        .trim()
                        .split('|')
                        .filter(Boolean)
                        .map((c) => c.trim())
                )
                i++
            }

            elements.push(
                <div key={`tbl-${i}`} className="my-1.5 overflow-x-auto">
                    <table className="min-w-full text-xs border-collapse">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-white/10">
                                {headerCells.map((cell, ci) => (
                                    <th key={ci} className="px-2 py-1 text-left font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                        {renderInline(cell, `th-${i}-${ci}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {bodyRows.map((row, ri) => (
                                <tr key={ri} className="border-b border-gray-100 dark:border-white/5">
                                    {row.map((cell, ci) => (
                                        <td key={ci} className="px-2 py-1 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {renderInline(cell, `td-${i}-${ri}-${ci}`)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
            continue
        }

        // Horizontal rule
        if (HR_REGEX.test(trimmed)) {
            elements.push(<hr key={`hr-${i}`} className="my-2 border-gray-200 dark:border-white/10" />)
            i++
            continue
        }

        // ## Heading
        const h2Match = trimmed.match(H2_REGEX)
        if (h2Match) {
            elements.push(
                <h3 key={`h2-${i}`} className="text-sm font-bold mt-2 mb-1">
                    {renderInline(h2Match[1], `h2i-${i}`)}
                </h3>
            )
            i++
            continue
        }

        // ### Subheading
        const h3Match = trimmed.match(H3_REGEX)
        if (h3Match) {
            elements.push(
                <h4 key={`h3-${i}`} className="text-xs font-bold mt-1.5 mb-0.5">
                    {renderInline(h3Match[1], `h3i-${i}`)}
                </h4>
            )
            i++
            continue
        }

        // Bullet list — group consecutive lines
        if (BULLET_REGEX.test(trimmed)) {
            const rawItems: { text: string; idx: number }[] = []
            while (i < lines.length) {
                const bMatch = lines[i].trim().match(BULLET_REGEX)
                if (!bMatch) break
                rawItems.push({ text: bMatch[1], idx: i })
                i++
            }
            const isCheckList = rawItems.some(({ text }) => CHECKBOX_CHECKED_REGEX.test(text) || CHECKBOX_UNCHECKED_REGEX.test(text))
            const items = rawItems.map(({ text, idx }) => {
                const checkedM = CHECKBOX_CHECKED_REGEX.exec(text)
                const uncheckedM = !checkedM ? CHECKBOX_UNCHECKED_REGEX.exec(text) : null
                if (checkedM) {
                    return (
                        <li key={`li-${idx}`} className="flex items-start gap-1.5">
                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500 dark:text-green-400" />
                            <span className="line-through text-slate-400 decoration-slate-300 dark:text-slate-500">
                                {renderInline(checkedM[2], `lii-${idx}`)}
                            </span>
                        </li>
                    )
                }
                if (uncheckedM) {
                    return (
                        <li key={`li-${idx}`} className="flex items-start gap-1.5">
                            <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600" />
                            <span>{renderInline(uncheckedM[1], `lii-${idx}`)}</span>
                        </li>
                    )
                }
                return <li key={`li-${idx}`}>{renderInline(text, `lii-${idx}`)}</li>
            })
            elements.push(
                <ul key={`ul-${i}`} className={isCheckList ? 'my-1.5 space-y-1' : 'my-1 list-disc space-y-0.5 pl-4'}>
                    {items}
                </ul>
            )
            continue
        }

        // Numbered list — group consecutive lines
        if (NUMBERED_REGEX.test(trimmed)) {
            const items: ReactElement[] = []
            while (i < lines.length) {
                const nMatch = lines[i].trim().match(NUMBERED_REGEX)
                if (!nMatch) break
                items.push(<li key={`nli-${i}`}>{renderInline(nMatch[1], `nlii-${i}`)}</li>)
                i++
            }
            elements.push(
                <ol key={`ol-${i}`} className="list-decimal pl-4 space-y-0.5 my-1">
                    {items}
                </ol>
            )
            continue
        }

        // Section label — plain line ending with ':'
        if (trimmed.endsWith(':') && !trimmed.startsWith('**') && !trimmed.includes('`')) {
            const color = getSectionLabelColor(trimmed)
            elements.push(
                <p key={`sl-${i}`} className={`mt-3 mb-0.5 text-[11px] font-semibold uppercase tracking-wide ${color}`}>
                    {renderInline(trimmed.slice(0, -1), `sli-${i}`)}
                </p>
            )
            i++
            continue
        }

        // Plain text paragraph
        elements.push(
            <p key={`p-${i}`} className="leading-relaxed">
                {renderInline(trimmed, `pi-${i}`)}
            </p>
        )
        i++
    }

    return <div className="space-y-1">{elements}</div>
}
